import { STEPS } from "../../../constants"
import { getVoyageurLangDisplayCode, setVoyageurLanguage } from "../../../core/utils"
import {
  VOYAGEUR_SUPPORTED_LANGS,
  getSessionVoyageurLang,
  normalizeVoyageurLangCode
} from "../../../core/voyageurLanguage"
import { TunnelDataContext } from "../../../digital-journey/context/TunnelDataContext"
import { apigateway } from "../../../intances/apigateway"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Dropdown, Modal } from "react-bootstrap"

const SWIPE_CLOSE_PX = 60

/** Code ISO pays pour le drapeau (aligné PDI : langue → pays du drapeau). */
const LANG_FLAG_COUNTRY = {
  fr: "fr",
  en: "gb",
  it: "it",
  pt: "pt",
  es: "es",
  nl: "nl"
}

function getLanguageDisplay(lng) {
  if (lng != null && typeof lng !== "string") {
    return { code: "FR", flagCountry: "fr" }
  }
  const normalized = String(lng || "fr-FR").trim()
  const [langPart] = normalized.split("-")
  const lang = (langPart || "fr").toLowerCase()
  return {
    code: lang.toUpperCase(),
    flagCountry: LANG_FLAG_COUNTRY[lang] || "fr"
  }
}

function isLanguageSelectorLocked(tunnelData) {
  const breadcrumbs = tunnelData?.breadcrumbs
  if (!Array.isArray(breadcrumbs)) return false

  const currentStepIndex = breadcrumbs.findIndex((step) => step.id === tunnelData?.step)
  const netheosStepIndex = breadcrumbs.findIndex((step) => step.id === STEPS.NETHEOS)

  return netheosStepIndex >= 0 && currentStepIndex >= netheosStepIndex
}

const LangFlag = React.memo(function LangFlag({ country }) {
  return (
    <img
      className="header-lang-selector__flag"
      src={`https://flagcdn.com/w20/${country}.png`}
      srcSet={`https://flagcdn.com/w40/${country}.png 2x`}
      alt=""
      aria-hidden="true"
      width="20"
      height="15"
      loading="lazy"
    />
  )
})

const LangDisplay = React.memo(function LangDisplay({ lng }) {
  const { code, flagCountry } = getLanguageDisplay(lng)
  return (
    <span className="header-lang-selector__label">
      <LangFlag country={flagCountry} />
      <span className="header-lang-selector__code">{code}</span>
    </span>
  )
})

function HeaderLanguageSelector() {
  const tunnelCtx = useContext(TunnelDataContext)
  const supportedFromTunnel = tunnelCtx?.tunnelData?.supportedLanguages
  const tunnelData = tunnelCtx?.tunnelData
  const setTunnelData = tunnelCtx?.setTunnelData

  const selectorLangs = useMemo(() => {
    if (!Array.isArray(supportedFromTunnel) || supportedFromTunnel.length === 0) {
      return VOYAGEUR_SUPPORTED_LANGS
    }
    const normalized = supportedFromTunnel.map((c) => normalizeVoyageurLangCode(c)).filter(Boolean)
    const uniq = [...new Set(normalized)]
    return uniq.length > 0 ? uniq : VOYAGEUR_SUPPORTED_LANGS
  }, [supportedFromTunnel])

  const [, setTick] = useState(0)
  const [sheetOpen, setSheetOpen] = useState(false)
  const touchStartY = useRef(null)

  useEffect(() => {
    const onLang = () => setTick((t) => t + 1)
    window.addEventListener("voyageurLanguageChange", onLang)
    return () => window.removeEventListener("voyageurLanguageChange", onLang)
  }, [])

  const displayLang = getVoyageurLangDisplayCode()
  const currentLng = getSessionVoyageurLang() || "fr-FR"
  const languageLocked = isLanguageSelectorLocked(tunnelData)

  const applyLanguage = useCallback(
    (lng) => {
      if (isLanguageSelectorLocked(tunnelData)) return

      setVoyageurLanguage(lng, selectorLangs)
      setSheetOpen(false)

      try {
        const order = tunnelData?.data?.order
        const opl = order?.opl || tunnelData?.opl

        if (order && typeof setTunnelData === "function") {
          setTunnelData({
            data: {
              ...tunnelData.data,
              order: {
                ...order,
                orderLanguageTag: lng
              }
            }
          })
        }

        if (!opl) return

        // Async, non bloquant: ne jamais bloquer l'UI si ça échoue.
        apigateway(tunnelData)
          .put(`/middle-business-service/api/v2/order/${opl}/order-language-tag`, {
            orderLanguageTag: lng,
            source: "client_selector"
          })
          .catch(() => {})
      } catch {
        // ignore
      }
    },
    [selectorLangs, setTunnelData, tunnelData]
  )

  const onTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0] ? e.touches[0].clientY : null
  }, [])

  const onTouchEnd = useCallback((e) => {
    if (touchStartY.current == null) return
    const endY = e.changedTouches[0] ? e.changedTouches[0].clientY : touchStartY.current
    const delta = endY - touchStartY.current
    touchStartY.current = null
    if (delta > SWIPE_CLOSE_PX) {
      setSheetOpen(false)
    }
  }, [])

  if (languageLocked || selectorLangs.length <= 1) {
    return null
  }

  const sheetItems = selectorLangs.map((lng) => (
    <button
      key={lng}
      type="button"
      className={`list-group-item list-group-item-action text-center header-lang-selector__list-item${
        currentLng === lng ? " header-lang-selector__list-item--active" : ""
      }`}
      onClick={() => applyLanguage(lng)}
    >
      <LangDisplay lng={lng} />
    </button>
  ))

  return (
    <>
      <div className="header-lang-selector d-none d-lg-block">
        <Dropdown>
          <Dropdown.Toggle
            as="button"
            type="button"
            className="header-lang-selector__trigger"
            id="header-language-dropdown-voyageur"
          >
            <LangDisplay lng={displayLang} />
          </Dropdown.Toggle>
          <Dropdown.Menu alignRight className="header-lang-selector__menu">
            {selectorLangs.map((lng) => (
              <Dropdown.Item
                key={lng}
                className="header-lang-selector__item"
                active={currentLng === lng}
                onClick={() => applyLanguage(lng)}
              >
                <LangDisplay lng={lng} />
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="d-lg-none">
        <button
          type="button"
          className={`header-lang-selector__trigger${sheetOpen ? " header-lang-selector__trigger--pressed" : ""}`}
          onClick={() => setSheetOpen(true)}
          aria-expanded={sheetOpen}
          aria-haspopup="dialog"
        >
          <LangDisplay lng={displayLang} />
        </button>
        <Modal
          show={sheetOpen}
          onHide={() => setSheetOpen(false)}
          className="header-lang-selector__modal"
          dialogClassName="header-lang-selector__modal-dialog"
          contentClassName="header-lang-selector__modal-content"
        >
          <Modal.Header closeButton className="header-lang-selector__sheet-header border-bottom-0 pb-0">
            <div className="w-100 d-flex justify-content-center">
              <span className="header-lang-selector__sheet-handle" aria-hidden />
            </div>
          </Modal.Header>
          <Modal.Body
            className="header-lang-selector__sheet-body pt-2"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="list-group list-group-flush">{sheetItems}</div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  )
}

export default HeaderLanguageSelector
